import { COMPETITION_STATUSES, getCompetitionStatus } from "_competition/helpers/getCompetitionStatus";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getProjectImages } from "shared/pages/project/sections/ps-image";
import { StyledImg } from "../styled";
import { CompetitionCoverOrientation, CompetitionCoverSize } from "../types/types";
import { Competition, Project } from "@brg/api";
import { useRouteMatch } from "react-router-dom";
import { RatingImgGenerate } from "shared/pages/opencallsPage/GetCompetitionItem/components/RatingImgGenerate";
import { ZharKnigaBadge } from "shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaBadge";
import { ZharKnigaAwardsPlateBadge } from "shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaAwardsPlateBadge";
import { useZharKnigaWinner } from "shared/hooks/useZharKnigaWinner";
import { useZharKnigaAwardsWinner } from "shared/hooks/useZharKnigaAwardsWinner";
import styled from "styled-components";
import { ExtraGridConditionType } from "shared/pages/opencallsPage/GetCompetitionItem/types/extraGridConditionType";
import {STATUSES} from "../../../const";

const ImageContainer = styled.div`
    position: relative;
    height: 100%;
`;

const BadgesContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 10;
`;

const WIDE_PROJECT_TYPES = ['fixed-ratio-small', 'fixed-ratio-large', 't-shape'] as const;

interface WinnerProject {
    images: ReturnType<typeof getProjectImages>;
    name: string;
    project: Project<'Local'> | Project<'Global'>;
}

const transformOrientation = (size: number): CompetitionCoverOrientation => {
    return [CompetitionCoverSize.wide, CompetitionCoverSize.square].includes(size) 
    ? CompetitionCoverOrientation.column 
    : CompetitionCoverOrientation.row
}

const getRatingCompetition = (project: Project<'Local'> | Project<'Global'> | undefined): Competition | undefined => {
    if (!project) return undefined
    const competitions = (project as any)?.competitions as Competition[] | undefined
    return competitions?.find((item: Competition) => item.contexts?.includes("ratings"))
};

const checkIsRatingWinner = (
    ratingCompetition: Competition | undefined,
    projectId: string | undefined
): boolean => {
    if (!ratingCompetition || !projectId) return false
    const winnerPositions = (ratingCompetition as any)?.nominations?.[0]?.winnerPositions as Array<{ winnerId: string }> | undefined;
    return winnerPositions?.some((item) => item?.winnerId === projectId) ?? false
}

interface CompetitionCoverProps {
    competition?: Competition<'Local'>,
    extraGridCondition?: ExtraGridConditionType;
    onChangeOrientation?: (orientation: CompetitionCoverOrientation) => void
    isMainPage?: boolean;
}

export const CompetitionCover: FC<CompetitionCoverProps> = ({ competition, onChangeOrientation, extraGridCondition, isMainPage: isMainPageFromGrid }) => {
    const { i18n } = useTranslation()
    const [coverOpt, setCoverOpt] = useState<CompetitionCoverSize | null>(null)
    const [onhoverPlate, setOnhoverPlate] = useState(true)

    const titleImages = useMemo(() => {
        if (competition?.isBilingual) {
            return i18n.language === 'en' ? competition?.titleImages_en : competition?.titleImages
        }
        return competition?.titleImages;
    }, [competition, i18n.language])


    const isWideProject = useMemo(() => {
        const nomination = competition?.nominations?.[0] as any;
        const projectType = nomination?.winners?.[0]?.projectType as string | undefined
        return projectType ? WIDE_PROJECT_TYPES.includes(projectType as typeof WIDE_PROJECT_TYPES[number]) : false
    }, [competition]);

    const isCurrentCompetitionPage = !!useRouteMatch('/competition')

    const isMainPage = useMemo(() => {
        return !isCurrentCompetitionPage && !competition?.parentCompetitionId
    } , [competition, isCurrentCompetitionPage])

    const winnerProject = useMemo((): WinnerProject | null => {
        if (!competition?.nominations?.length) {
            return null;
        }

        const nomination = competition.nominations[0] as any;
        if (!nomination?.winners?.length) {
            return null;
        }

        const winner = nomination.winners[0] as Project<'Local'> | Project<'Global'>;
        return {
            images: getProjectImages(winner, i18n.language),
            name: `${winner?.author?.name || ''} ${winner?.author?.surName || ''}`.trim(),
            project: winner
        };
    }, [competition, i18n.language])

    const winnerProjectData = winnerProject?.project;
    const ratingCompetition = useMemo(() => getRatingCompetition(winnerProjectData), [winnerProjectData])
    const isRatingWinner = useMemo(
        () => checkIsRatingWinner(ratingCompetition, winnerProjectData?.projectId),
        [ratingCompetition, winnerProjectData?.projectId]
    );
    
    const { isWinner: isZharKnigaWinner } = useZharKnigaWinner(winnerProjectData?.projectId)
    const { isWinner: isZharKnigaAwardsWinner } = useZharKnigaAwardsWinner(winnerProjectData?.projectId)

    const competitionStatus = useMemo(() => getCompetitionStatus(competition), [competition])
    const hasCustomCover = !!(competition as any)?.lmsModuleProjectId && !!titleImages?.length
    const isWideCover = !hasCustomCover && !!(competitionStatus === COMPETITION_STATUSES.full_finish && winnerProject && isWideProject)
    const isRatingCompetition = !!(competition as any)?.isRating

    const files = useMemo(() => {
        if (isRatingCompetition) {
            return undefined
        }
        if (hasCustomCover) {
            return titleImages;
        }
        return competitionStatus === COMPETITION_STATUSES.full_finish && winnerProject
            ? winnerProject.images
            : titleImages;
    }, [isRatingCompetition, hasCustomCover, competitionStatus, winnerProject, titleImages])

    const setImageRatio = useCallback((ratio: number) => {
        if (ratio < CompetitionCoverSize.default) {
            setCoverOpt(CompetitionCoverSize.default)
        } else if (ratio >= CompetitionCoverSize.default && ratio <= CompetitionCoverSize.wide) {
            setCoverOpt(CompetitionCoverSize.wide)
        } else if (ratio > CompetitionCoverSize.wide) {
            setCoverOpt(CompetitionCoverSize.square)
        }
    }, [])

    useEffect(() => {
        if (coverOpt !== null) {
            onChangeOrientation?.(transformOrientation(coverOpt));
        }
    }, [coverOpt, onChangeOrientation])

    const shouldShowBadges = competitionStatus === COMPETITION_STATUSES.full_finish
        && winnerProject
        && (isRatingWinner || isZharKnigaWinner || isZharKnigaAwardsWinner)
        && !isMainPageFromGrid;

    const handleMouseEnter = useCallback(() => setOnhoverPlate(false), [])
    const handleMouseLeave = useCallback(() => setOnhoverPlate(true), [])

    const hideBadge = ratingCompetition?.status === STATUSES['Completed']
    return (
        <ImageContainer
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {isRatingCompetition ? (
                <RatingImgGenerate competition={competition} isCompetitionPage={isCurrentCompetitionPage} />
            ) : (
                <>
                    {shouldShowBadges && !hideBadge && (
                        <BadgesContainer>
                            {isRatingWinner && (ratingCompetition as any)?.isRating && (
                                <RatingImgGenerate 
                                    competition={ratingCompetition} 
                                    isGridPlate={true} 
                                    onhoverPlate={!onhoverPlate} 
                                />
                            )}
                            {isZharKnigaWinner && (
                                <ZharKnigaBadge 
                                    isGridBadge={true} 
                                    onhoverPlate={!onhoverPlate} 
                                />
                            )}
                            {isZharKnigaAwardsWinner && (
                                <ZharKnigaAwardsPlateBadge
                                    isGridPlate={true}
                                    onhoverPlate={!onhoverPlate}
                                />
                            )}
                        </BadgesContainer>
                    )}
                    <StyledImg
                        extraGridCondition={extraGridCondition}
                        isMainPage={isMainPage}
                        getImageRatio={setImageRatio}
                        coverOpt={coverOpt || undefined} 
                        isWideProject={isWideCover} 
                        files={files} 
                        competition={competition}
                    />
                </>
            )}
        </ImageContainer>
    )
}import { COMPETITION_STATUSES, getCompetitionStatus } from "_competition/helpers/getCompetitionStatus";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getProjectImages } from "shared/pages/project/sections/ps-image";
import { StyledImg } from "../styled";
import { CompetitionCoverOrientation, CompetitionCoverSize } from "../types/types";
import { Competition, Project } from "@brg/api";
import { useRouteMatch } from "react-router-dom";
import { RatingImgGenerate } from "shared/pages/opencallsPage/GetCompetitionItem/components/RatingImgGenerate";
import { ZharKnigaBadge } from "shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaBadge";
import { ZharKnigaAwardsPlateBadge } from "shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaAwardsPlateBadge";
import { useZharKnigaWinner } from "shared/hooks/useZharKnigaWinner";
import { useZharKnigaAwardsWinner } from "shared/hooks/useZharKnigaAwardsWinner";
import styled from "styled-components";
import { ExtraGridConditionType } from "shared/pages/opencallsPage/GetCompetitionItem/types/extraGridConditionType";
import {STATUSES} from "../../../const";

const ImageContainer = styled.div`
    position: relative;
    height: 100%;
`;

const BadgesContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 10;
`;

const WIDE_PROJECT_TYPES = ['fixed-ratio-small', 'fixed-ratio-large', 't-shape'] as const;

interface WinnerProject {
    images: ReturnType<typeof getProjectImages>;
    name: string;
    project: Project<'Local'> | Project<'Global'>;
}

const transformOrientation = (size: number): CompetitionCoverOrientation => {
    return [CompetitionCoverSize.wide, CompetitionCoverSize.square].includes(size) 
    ? CompetitionCoverOrientation.column 
    : CompetitionCoverOrientation.row
}

const getRatingCompetition = (project: Project<'Local'> | Project<'Global'> | undefined): Competition | undefined => {
    if (!project) return undefined
    const competitions = (project as any)?.competitions as Competition[] | undefined
    return competitions?.find((item: Competition) => item.contexts?.includes("ratings"))
};

const checkIsRatingWinner = (
    ratingCompetition: Competition | undefined,
    projectId: string | undefined
): boolean => {
    if (!ratingCompetition || !projectId) return false
    const winnerPositions = (ratingCompetition as any)?.nominations?.[0]?.winnerPositions as Array<{ winnerId: string }> | undefined;
    return winnerPositions?.some((item) => item?.winnerId === projectId) ?? false
}

interface CompetitionCoverProps {
    competition?: Competition<'Local'>,
    extraGridCondition?: ExtraGridConditionType;
    onChangeOrientation?: (orientation: CompetitionCoverOrientation) => void
    isMainPage?: boolean;
}

export const CompetitionCover: FC<CompetitionCoverProps> = ({ competition, onChangeOrientation, extraGridCondition, isMainPage: isMainPageFromGrid }) => {
    const { i18n } = useTranslation()
    const [coverOpt, setCoverOpt] = useState<CompetitionCoverSize | null>(null)
    const [onhoverPlate, setOnhoverPlate] = useState(true)

    const titleImages = useMemo(() => {
        if (competition?.isBilingual) {
            return i18n.language === 'en' ? competition?.titleImages_en : competition?.titleImages
        }
        return competition?.titleImages;
    }, [competition, i18n.language])


    const isWideProject = useMemo(() => {
        const nomination = competition?.nominations?.[0] as any;
        const projectType = nomination?.winners?.[0]?.projectType as string | undefined
        return projectType ? WIDE_PROJECT_TYPES.includes(projectType as typeof WIDE_PROJECT_TYPES[number]) : false
    }, [competition]);

    const isCurrentCompetitionPage = !!useRouteMatch('/competition')

    const isMainPage = useMemo(() => {
        return !isCurrentCompetitionPage && !competition?.parentCompetitionId
    } , [competition, isCurrentCompetitionPage])

    const winnerProject = useMemo((): WinnerProject | null => {
        if (!competition?.nominations?.length) {
            return null;
        }

        const nomination = competition.nominations[0] as any;
        if (!nomination?.winners?.length) {
            return null;
        }

        const winner = nomination.winners[0] as Project<'Local'> | Project<'Global'>;
        return {
            images: getProjectImages(winner, i18n.language),
            name: `${winner?.author?.name || ''} ${winner?.author?.surName || ''}`.trim(),
            project: winner
        };
    }, [competition, i18n.language])

    const winnerProjectData = winnerProject?.project;
    const ratingCompetition = useMemo(() => getRatingCompetition(winnerProjectData), [winnerProjectData])
    const isRatingWinner = useMemo(
        () => checkIsRatingWinner(ratingCompetition, winnerProjectData?.projectId),
        [ratingCompetition, winnerProjectData?.projectId]
    );
    
    const { isWinner: isZharKnigaWinner } = useZharKnigaWinner(winnerProjectData?.projectId)
    const { isWinner: isZharKnigaAwardsWinner } = useZharKnigaAwardsWinner(winnerProjectData?.projectId)

    const competitionStatus = useMemo(() => getCompetitionStatus(competition), [competition])
    const hasCustomCover = !!(competition as any)?.lmsModuleProjectId && !!titleImages?.length
    const isWideCover = !hasCustomCover && !!(competitionStatus === COMPETITION_STATUSES.full_finish && winnerProject && isWideProject)
    const isRatingCompetition = !!(competition as any)?.isRating

    const files = useMemo(() => {
        if (isRatingCompetition) {
            return undefined
        }
        if (hasCustomCover) {
            return titleImages;
        }
        return competitionStatus === COMPETITION_STATUSES.full_finish && winnerProject
            ? winnerProject.images
            : titleImages;
    }, [isRatingCompetition, hasCustomCover, competitionStatus, winnerProject, titleImages])

    const setImageRatio = useCallback((ratio: number) => {
        if (ratio < CompetitionCoverSize.default) {
            setCoverOpt(CompetitionCoverSize.default)
        } else if (ratio >= CompetitionCoverSize.default && ratio <= CompetitionCoverSize.wide) {
            setCoverOpt(CompetitionCoverSize.wide)
        } else if (ratio > CompetitionCoverSize.wide) {
            setCoverOpt(CompetitionCoverSize.square)
        }
    }, [])

    useEffect(() => {
        if (coverOpt !== null) {
            onChangeOrientation?.(transformOrientation(coverOpt));
        }
    }, [coverOpt, onChangeOrientation])

    const shouldShowBadges = competitionStatus === COMPETITION_STATUSES.full_finish
        && winnerProject
        && (isRatingWinner || isZharKnigaWinner || isZharKnigaAwardsWinner)
        && !isMainPageFromGrid;

    const handleMouseEnter = useCallback(() => setOnhoverPlate(false), [])
    const handleMouseLeave = useCallback(() => setOnhoverPlate(true), [])

    const hideBadge = ratingCompetition?.status === STATUSES['Completed']
    return (
        <ImageContainer
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {isRatingCompetition ? (
                <RatingImgGenerate competition={competition} isCompetitionPage={isCurrentCompetitionPage} />
            ) : (
                <>
                    {shouldShowBadges && !hideBadge && (
                        <BadgesContainer>
                            {isRatingWinner && (ratingCompetition as any)?.isRating && (
                                <RatingImgGenerate 
                                    competition={ratingCompetition} 
                                    isGridPlate={true} 
                                    onhoverPlate={!onhoverPlate} 
                                />
                            )}
                            {isZharKnigaWinner && (
                                <ZharKnigaBadge 
                                    isGridBadge={true} 
                                    onhoverPlate={!onhoverPlate} 
                                />
                            )}
                            {isZharKnigaAwardsWinner && (
                                <ZharKnigaAwardsPlateBadge
                                    isGridPlate={true}
                                    onhoverPlate={!onhoverPlate}
                                />
                            )}
                        </BadgesContainer>
                    )}
                    <StyledImg
                        extraGridCondition={extraGridCondition}
                        isMainPage={isMainPage}
                        getImageRatio={setImageRatio}
                        coverOpt={coverOpt || undefined} 
                        isWideProject={isWideCover} 
                        files={files} 
                        competition={competition}
                    />
                </>
            )}
        </ImageContainer>
    )
}